/** Import pour utiliser la librairy oidc-client-ts */
import { UserManager, User, WebStorageStateStore } from 'oidc-client-ts';

/** Types custom utilisé par le vuex */
import OidcAuthority from "@/types/OidcAuthority";

/** Import VUEX nécessaire pour le store */
import { VuexModule, Mutation, Action, Module } from 'vuex-module-decorators';

/** URL vers la racine de l'application DCX */
import { VUE_APP_URL } from '@/App.settings';

/**
 * Store Vuex chargé du maintien de la session OpenIdConnect.
 * 
 * Gère le chargement des informations de session OIDC en cas de rafraîchissement de la page.
 * Permet de se connecter, et de se déconnecter selon les étapes de la procédure d'authentification OpenIdConnect.
 * Gère l'abstraction "UserManager" fourni par la lib "oidc-client-ts"
 * 
 * OIDC : OpenIdConnect
 * Idp : Identity provider (autorité d'authentification tels que Azure Entre, Keycloak, ...)
 * Client = Application à laquelle se connecte l'utilisateur et faisant office d'intermédiaire entre l'utilisateur, la webAPI et l'Idp.
 * access_token = token récupèré par l'Idp utilisé par la web API pour valider l'authentification de l'utilisateur (peut contenir de claims spécifique)
 * Claims = Autorisations spécifiques nécessaires pour utiliser certains services. Elles sont définies dans l'Idp et consommées par la webAPI.
 * Serveur de ressources = Nom désignant la webAPI (Api délivrant des services en échange de token d'accès).
 * 
 * https://learn.microsoft.com/fr-fr/entra/identity-platform/v2-app-types
 * https://learn.microsoft.com/fr-fr/entra/identity-platform/access-tokens#validate-tokens
 * https://learn.microsoft.com/en-us/entra/identity-platform/security-best-practices-for-app-registration#identity-type   
 */
@Module({
    namespaced: true
})
class OidcStore extends VuexModule{
    
    /** Manager Oidc (oidc-client-ts) */
    private oidcManager!: UserManager;

//#region MUTATIONS

    /** Set le manager Oidc utiliser pour l'authentification Oidc */
    @Mutation
    private setOidcManager(oidcManager: UserManager){
        this.oidcManager = oidcManager;
    }

//#endregion MUTATIONS

//#region ACTIONS

    /** 
     * Charge le store (UserManager lib (oidc-client-ts)).
     * N'écrase pas l'initialisation en cours
     * La réinitialisation est à la charge de l'utilisateur.
     * @param configuration Configuration de l'Idp "Identity provider".
     */
    @Action({ rawError: true })
    public async Load(configuration: OidcAuthority){
        if(configuration){ /** Teste présence de la configuration */
            if(!this.oidcManager){ /** Teste si le  manager OIDC n'est pas déjà initialisé */
                const settings = {
                    authority: configuration.AuthorityUri,
                    client_id: configuration.ClientId, 
                    // client_secret: configuration.ClientSecret, /** Inutile pour une application SPA car un navigateur web appartenant à l'utilisateur n'est pas considéré comme un intermédiaire fiable */
                    redirect_uri: VUE_APP_URL,
                    response_type: 'code', /** demande de challenge PKCE pour fiabilisé l'echange avec l'Idp */
                    scope: `openid profile email ${ configuration.Scope ? configuration.Scope : "" }`,
                    automaticSilentRenew: true,
                    userStore: new WebStorageStateStore({ store: window.sessionStorage })
                };
                await this.context.commit('setOidcManager', new UserManager(settings));
            }
        }
    }

    /** Réinitialise les propriétés du "store" */
    @Action({ rawError: true })
    public Reset(){
        /** Purge les propriétés du "store". */
        this.context.commit('setOidcManager', undefined);
    }
    /** Réinitialise les propriétés du "store" et supprime le jeton d'identité. */
    @Action({ rawError: true })
    public async ResetSession(){
        if(this.oidcManager)
            await this.oidcManager.removeUser();
        await this.context.dispatch('Reset');
    }


    /** 
     * Déclenche la phase de "login" OIDC.
     * @param args Configuration OIDC et login saisi par l'utilisateur. Le login peut être "undefined".
     */
    @Action({ rawError: true })
	public async LoginOidc( args: LoginOidcConfig ): Promise<void> {
		if(!this.oidcManager) /** Vérifie l'initialisation (F5, rafraichissement de page, redirection, ...) */
            await this.context.dispatch('Load', args.configuration);

        if(this.oidcManager){ /** Test le manager en cas d'echec du load */
            const user = await this.oidcManager.getUser();
            if(!user || user.expired){ /** Test si l'utilisateur est non authentifié ou que sa session est expiré. */
                /** Demande de connexion auprès de l'Idp */
                await this.oidcManager.signinRedirect({
                    login_hint: args.login ? args.login : ""
                });
            }
        }
	}

    /** 
     * Exécuté après la redirection de l'Idp, post-login.
     * Gère le retour de l'autorité et traite l'identité, récupère le token.
     */
    @Action({ rawError: true })
	public async HandleOidcCallback(configuration: OidcAuthority): Promise<void> {
		if(!this.oidcManager) /** Vérifie l'initialisation (F5, rafraichissement de page, redirection, ...) */
			await this.context.dispatch('Load', configuration);

        if(this.oidcManager){ /** en cas d'echec du load */
            const user = await this.oidcManager.getUser();
            if(!user || user.expired){ /** Test si l'utilisateur est non authentifié ou que sa session est expiré. */
                await this.oidcManager.signinRedirectCallback();
            }
        }
	}

	/** Déclenche la phase de "logout" OIDC */
    @Action({ rawError: true })
	public async LogoutOidcIdp(configuration: OidcAuthority): Promise<void> {
        if(!this.oidcManager) /** Vérifie l'initialisation (F5, rafraichissement de page, redirection, ...) */
            await this.context.dispatch('Load', configuration);

        if(this.oidcManager) {
            /** Redirection vers l’IdP avec id_token_hint et post_logout_redirect_uri */ 
            await this.oidcManager.signoutRedirect({
                id_token_hint: (await this.oidcManager.getUser()).id_token,
                post_logout_redirect_uri: VUE_APP_URL + "SignOut"
            });
        }
	}
    /**
     * Exécuté après la redirection de l'Idp, post-logout.
     * Effectue le netoyage de session pour éviter d'avoir un session qui persiste dans le temps.
     */
    @Action({ rawError: true })
    public async HandleOidcLogoutCallback(configuration: OidcAuthority): Promise<void> {
        if(!this.oidcManager) /** Vérifie l'initialisation (F5, rafraichissement de page, redirection, ...) */
			await this.context.dispatch('Load', configuration);

        if(this.oidcManager) {
            await this.oidcManager.signoutRedirectCallback();
            await this.context.dispatch('Reset'); 
        }
    }
    
//#endregion ACTIONS

//#region GETTERS

    /** Récupérer l’utilisateur courant */ 
	public get GetUser(): Promise<User|undefined> {
        if(!this.oidcManager)
            return undefined;
		return this.oidcManager.getUser();
	}

//#endregion GETTERS

};

type LoginOidcConfig = {
    configuration: OidcAuthority
    login: string|undefined
};

export default OidcStore;